import { ConnextData, ConnextDataType, LoggerEnumType, MonitoringType, MonitorPrefEnumType, PowerFlowGraphMode, ProductModelService, System } from "ngx-core-services";
import { User } from "firebase/auth";

import { SystemDefinition } from "../state/system.state";

export interface SystemDefinitionConfiguration {
  monitoringType: MonitoringType;
  /**
   * It is undefined when it does not make sense to consider it
   */
  inverterMeterSensor?: boolean;
  tz: number;
  structure: PowerFlowGraphMode;
};

export interface SystemEvent {
  system: System;
  forceUpdateSystem: boolean;
  forceUpdateConnext: boolean;
}

export const extractSystemFromDefinition = (definition: SystemDefinition): System => {
  const { subscription, data, monitoringType, metadata, ...system } = definition;
  return system;
}

export const getSystemDefinitionConfiguration = (definition: SystemDefinition): SystemDefinitionConfiguration => {
  let { monitorPref, metadata, monitoringType } = definition;
  monitorPref = monitorPref ?? (monitoringType === 'Connext' ? MonitorPrefEnumType.CONNEXT : monitoringType === 'AzzurroHub' ? MonitorPrefEnumType.AZZURRO_HUB : MonitorPrefEnumType.SOLARMAN);

  return {
    monitoringType: monitorPref === 'CONNEXT' ? 'Connext' : monitorPref === MonitorPrefEnumType.AZZURRO_HUB ? 'AzzurroHub' : 'Solarman',
    inverterMeterSensor: metadata?.invertMeterSensor,
    tz: metadata?.tz || 2,
    structure: metadata?.structure || PowerFlowGraphMode.OnlyGeneratedPower,
  };
};

export const isSystemEqualToSystemDefinition = (system: System, systemDefinition: SystemDefinition): boolean => {
  const keysToCompare: (keyof System)[] = [
    'uid', 'connextSn', 'configuration', 'devices', 'img', 'name'
  ];

  for (const key of keysToCompare) {
    if (JSON.stringify(system[key]) !== JSON.stringify(systemDefinition[key])) {
      return false;
    }
  }

  return true;
}

export const getConnextDataFromConnextCertifiedPlant = (system: System, userFb: User): ConnextData => {
  const currentConnext = system.aws?.ems_state_v2?.find((ems) => {
    const id = ems.loggerId;
    const product = new ProductModelService(id);
    return product.type?.name === 'connext';
  }) as ConnextDataType;
  const connextId = currentConnext?.loggerId;
  const users = (system.aws?.users ?? []).map((user) => ({
    email: user.email,
    userLevel: user.userLevel,
    operator: false,
  }));

  if (!currentConnext || !connextId) {
    throw new Error('Connext not found');
  }

  const connextData: ConnextData = {
    id: connextId,
    info: currentConnext.info,
    devices: [...currentConnext.devices, ...currentConnext.evch],
    io: currentConnext.io,
    config: currentConnext.config,
    userLevel: users.find((user) => user.email === userFb.email)?.userLevel ?? 0,
    hasOwner: users.some((user) => user.userLevel === 2),
    operator: false,
    users,
  };

  return connextData;
}

export const forceVirtualSystemUpdate = (system: System) => ({
  system,
  forceUpdateSystem: true,
  /**
   * This is a workaround to force the update of the connext data
   * when systems is aws certified
   */
  forceUpdateConnext: !!system.aws?.ems,
})

export const forceConnextUpdate = (system: System) => ({
  system,
  forceUpdateSystem: false,
  forceUpdateConnext: true,
})


export function getSystemDefinitionDeviceIdsForTelemetries(
  system: SystemDefinition,
  opts: { onlyInstalled: boolean; onlyInvertersOfEmsMonitoring?: boolean } = { onlyInstalled: false, onlyInvertersOfEmsMonitoring: false }
): string[] {
  const systemConfiguration = getSystemDefinitionConfiguration(system);
  let inverterSerials = system.devices
    .filter((device) => device.type !== 'connext' && device.type !== 'azzurro-hub')
    .map((device) => device.id);

  if (!opts.onlyInstalled) {
    const verifiedUninstallations = (system.metadata?.verifiedUninstallations ?? []).map(
      (device) => device.sn
    );

    inverterSerials = inverterSerials.concat(verifiedUninstallations);
  }

  if (systemConfiguration.monitoringType === 'Connext' && system.configuration.connextId) {
    return opts.onlyInvertersOfEmsMonitoring
      ? inverterSerials
      : [system.configuration.connextId];
  }

  if (systemConfiguration.monitoringType === 'AzzurroHub' && system.configuration.azzurroHubId) {
    return opts.onlyInvertersOfEmsMonitoring
      ? inverterSerials
      : [system.configuration.azzurroHubId];
  }

  return inverterSerials;
}

export function getEmsConfig(system: SystemDefinition): { loggerId: string | undefined, type: LoggerEnumType } | undefined {
   // FOR CONNEXT IS MANDATORY TO PASS THE type CONNEXT AND loggerId undefined
  if ( system.configuration.connextId ) {
    return { loggerId: undefined, type: LoggerEnumType.Connext };
  }

  if ( system.configuration.azzurroHubId ) {
    return { loggerId: system.configuration.azzurroHubId, type: LoggerEnumType.Azzurro_Hub };
  }

  return undefined;
}